import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewModule as GridIcon,
  Description as PageIcon,
  Chat as AIChatIcon,
} from '@mui/icons-material';
import { useCanvasBySlug } from '../../data/canvas/useCanvases';
import { usePages, useDeletePage } from '../../data/canvas/usePages';
import { Page } from '../../types/canvas.types';
import { PageTitle } from '../PageTitle';
import { BrowserTabTitle } from '../BrowserTabTitle';

interface PageCardProps {
  page: any; // TODO: Import Page type
  accountId: string;
  canvasSlug: string;
  onEdit: (pageId: string) => void;
  onDelete: (pageId: string) => void;
}

const PageCard: React.FC<PageCardProps> = ({
  page,
  accountId,
  canvasSlug,
  onEdit,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePageClick = () => {
    navigate(`/${accountId}/canvases/${canvasSlug}/${page.slug}`);
  };

  const getLayoutIcon = (layoutType: string) => {
    switch (layoutType) {
      case 'grid':
        return <GridIcon />;
      case 'page':
        return <PageIcon />;
      case 'ai_chat':
        return <AIChatIcon />;
      default:
        return <GridIcon />;
    }
  };

  const getLayoutLabel = (layoutType: string) => {
    switch (layoutType) {
      case 'grid':
        return 'Grid Layout';
      case 'page':
        return 'Page Layout';
      case 'ai_chat':
        return 'AI Chat';
      default:
        return 'Grid Layout';
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent
        sx={{ flexGrow: 1, cursor: 'pointer' }}
        onClick={handlePageClick}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
        >
          <Typography variant="h6" component="h3" gutterBottom>
            {page.title}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuOpen(e);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          /{page.slug}
        </Typography>

        <Box display="flex" alignItems="center" gap={1} mt={2}>
          {getLayoutIcon(page.layout_type)}
          <Chip
            label={getLayoutLabel(page.layout_type)}
            size="small"
            variant="outlined"
          />
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            onEdit(page.id);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Page
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(page.id);
            handleMenuClose();
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Page
        </MenuItem>
      </Menu>
    </Card>
  );
};

export const CanvasView: React.FC = () => {
  const { accountId, canvasSlug } = useParams();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<Page | null>(null);

  const {
    data: canvas,
    isLoading,
    error,
  } = useCanvasBySlug(accountId!, canvasSlug!);
  const { data: pages, isLoading: pagesLoading } = usePages(
    accountId!,
    canvas?.id || '',
  );
  const deletePageMutation = useDeletePage(canvas?.id || '', accountId!);

  // Automatically redirect to first page when pages are loaded
  useEffect(() => {
    if (!pagesLoading && pages && pages.length > 0) {
      const firstPage = pages[0];
      navigate(`/${accountId}/canvases/${canvasSlug}/${firstPage.slug}`, {
        replace: true,
      });
    }
  }, [pages, pagesLoading, accountId, canvasSlug, navigate]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Error loading canvas: {error.message}</Alert>
    );
  }

  if (!canvas) {
    return <Alert severity="warning">Canvas not found</Alert>;
  }

  const handleCreatePage = () => {
    navigate(`/${accountId}/canvases/${canvasSlug}/pages/create`);
  };

  const handleEditPage = (pageId: string) => {
    // TODO: Implement page editing
    console.log('Edit page:', pageId);
  };

  const handleDeletePage = (pageId: string) => {
    const page = pages?.find((p) => p.id === pageId);
    if (page) {
      setPageToDelete(page);
      setDeleteConfirmOpen(true);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <>
      <BrowserTabTitle>{canvas.name}</BrowserTabTitle>
      <PageTitle text={canvas.name} />
      <Box sx={{ p: 3 }}>
        {/* Canvas Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              {canvas.name}
            </Typography>
            {canvas.settings?.['description'] && (
              <Typography variant="body1" color="text.secondary">
                {canvas.settings['description']}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreatePage}
          >
            Add Page
          </Button>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="Pages" />
            <Tab label="Settings" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {selectedTab === 0 && (
          <Box>
            {pagesLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : pages && pages.length > 0 ? (
              <Grid container spacing={3}>
                {pages.map((page) => (
                  <Grid item xs={12} sm={6} md={4} key={page.id}>
                    <PageCard
                      page={page}
                      accountId={accountId!}
                      canvasSlug={canvasSlug!}
                      onEdit={handleEditPage}
                      onDelete={handleDeletePage}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="300px"
                textAlign="center"
              >
                <PageIcon
                  sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No pages yet
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Create your first page to start building your canvas.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreatePage}
                >
                  Create First Page
                </Button>
              </Box>
            )}
          </Box>
        )}

        {selectedTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Canvas Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Canvas settings will be available here.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete Page Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPageToDelete(null);
        }}
        maxWidth="sm"
      >
        <DialogTitle>Delete Page?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the page{' '}
            <strong>{pageToDelete?.title || 'Untitled Page'}</strong>? All
            widgets on this page will also be deleted. This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setPageToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (pageToDelete) {
                try {
                  await deletePageMutation.mutateAsync(pageToDelete.id);
                  setDeleteConfirmOpen(false);
                  setPageToDelete(null);
                } catch (err) {
                  console.error('Failed to delete page:', err);
                }
              }
            }}
            color="error"
            variant="contained"
            disabled={deletePageMutation.isPending}
          >
            {deletePageMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
